import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2Icon } from 'lucide-react';
import { AuthLayout } from '../../layouts/AuthLayout';
import { updatePassword } from '../../services/authService';
import { supabase } from '../../services/supabaseClient';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';

/**
 * Pantalla a la que lleva el enlace del correo de recuperación.
 * Supabase abre esta URL con una sesión temporal de tipo `recovery`, que es la
 * que permite guardar una contraseña nueva.
 */
export function ResetPassword() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [valid, setValid] = useState(false);
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    // El token viaja en el hash de la URL; supabase-js lo canjea al cargar.
    supabase.auth.getSession().then(({
      data
    }) => {
      if (!active) return;
      setValid(Boolean(data.session));
      setReady(true);
    });
    const {
      data: listener
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      if (session) setValid(true);
      setReady(true);
    });
    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);
  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (saving) return;
    setError(null);
    if (password !== repeat) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setSaving(true);
    try {
      await updatePassword(password);
      setDone(true);
      window.setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  }
  return <AuthLayout title="Nueva contraseña" subtitle="Crea una contraseña nueva para tu cuenta." footer={<Link to="/login" className="font-medium text-primary hover:underline" data-id="element-1796" data-mp="13advez3lyuaw">
          Volver a iniciar sesión
        </Link>} data-id="element-1795" data-mp="1h1se14tlsthf">
      {done ? <div className="rounded-lg border border-border bg-surface p-6 text-center" data-id="element-1797" data-mp="17bz4e9d0uoft">
          <CheckCircle2Icon className="mx-auto mb-3 h-8 w-8 text-green-600" aria-hidden="true" data-id="element-1798" data-mp="1v2bmgd1ze1k4" />
          <p className="text-sm text-foreground" data-id="element-1799" data-mp="1zz1y8o6r6azq">
            Contraseña actualizada. Te llevamos a iniciar sesión…
          </p>
        </div> : !ready ? <div className="flex justify-center py-8" aria-busy="true" data-id="element-1800" data-mp="1m2ohjha1mdmb">
          <span className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" data-id="element-1801" data-mp="1qxviihbxh47j" />
        </div> : !valid ? <div className="rounded-lg border border-border bg-surface p-6 text-center" data-id="element-1802" data-mp="an9p8qss49xj">
          <p className="text-sm text-foreground" data-id="element-1803" data-mp="flkzh2mzkmga">
            Este enlace ha caducado o ya se ha utilizado.
          </p>
          <Link to="/recuperar-contrasena" className="mt-4 inline-block" data-id="element-1804" data-mp="753uxhuhy38n">
            <Button variant="secondary" data-id="element-1805" data-mp="31e7wwtgztpq">Pedir un enlace nuevo</Button>
          </Link>
        </div> : <form onSubmit={handleSubmit} className="space-y-5" noValidate data-id="element-1806" data-mp="h0x4qtgtzimq">
          <Input id="password" label="Nueva contraseña" type="password" autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} data-id="element-1807" data-mp="c3dq3sg813u9" />
          <Input id="repeat" label="Repetir contraseña" type="password" autoComplete="new-password" value={repeat} onChange={e => setRepeat(e.target.value)} data-id="element-1808" data-mp="3fj2q4h9n9kl3" />
          {error && <p role="alert" className="text-sm font-medium text-red-600" data-id="element-1809" data-mp="3u99yu59unp8l">
              {error}
            </p>}
          <Button type="submit" size="lg" loading={saving} className="w-full" data-id="element-1810" data-mp="3pf5pxgh0x74v">
            Guardar contraseña
          </Button>
        </form>}
    </AuthLayout>;
}